import React, { createContext, useContext, useState, useEffect } from "react";
import { io } from "socket.io-client";

// Create context
const WebSocketContext = createContext(null);

export const WebSocketProvider = ({ children }) => {
    const [socket, setSocket] = useState(null);
    const [assetTrackerData, setAssetTrackerData] = useState(null);
    const [levelMonitorData, setLevelMonitorData] = useState(null);
    const [holyKelData, setHolyKelData] = useState(null);
    const [serviceRequestData, setServiceRequestData] = useState(null);
    const [alertData, setAlertData] = useState(null);
    const [jobState, setJobState] = useState(null);
    const [linkedDevice, setLinkedDevice] = useState(null);
    // New state for tablet telemetry data (device_type_id = 10)
    const [tabletTelemetryData, setTabletTelemetryData] = useState(null);
    const [tabletTransferTableData, setTabletTransferTableData] = useState(null);
    const [tabletTransferUpdated, setTabletTransferUpdated] = useState(null);
    const [reconnectAttempts, setReconnectAttempts] = useState(0);
    const maxReconnectAttempts = 50;
    const reconnectDelay = 10000;

    const getUserToken = () => {
        return localStorage.getItem('token')
    }

    useEffect(() => {
        const socketInstance = io('https://app.rivio.io', {
            reconnection: true,
            reconnectionAttempts: maxReconnectAttempts,
            reconnectionDelay: reconnectDelay,
            auth: {
                token: getUserToken(), // from localStorage/cookies/etc.
            }
        });
    
        socketInstance.on("connect", () => {
            console.log("Socket.IO connected");
            setReconnectAttempts(0);
            setSocket(socketInstance);
        });
    
        socketInstance.on("assetTrackerWebhook", (data) => {
            console.log("Centralized assetTrackerWebhook:", data);
            setAssetTrackerData({ ...data });
        });
    
        socketInstance.on("levelMonitorWebhook", (data) => {
            console.log("Centralized levelMonitorWebhook:", data);
            setLevelMonitorData({ ...data });
        });

        socketInstance.on("gatewayWebhook", (data) => {
            console.log("Centralized gatewayWebhook:", data);
            setHolyKelData({ ...data });
        });

        socketInstance.on("serviceRequest", (data) => {
            console.log("Centralized serviceRequest:", data);
            setServiceRequestData({ ...data });
        });

        socketInstance.on("alert", (data) => {
            console.log("Centralized alert:", data);
            setAlertData({ ...data });
        });

        socketInstance.on("jobState", (data) => {
            console.log("Centralized jobState:", data);
            setJobState({ ...data });
        });

        socketInstance.on("linkedDeviceWebhook", (data) => {
            console.log("Centralized linkedDeviceWebhook:", data);
            setLinkedDevice({ ...data });
        });

        // New listener for tablet telemetry data (device_type_id = 10)
        socketInstance.on("tabletTelemetryWebhook", (data) => {
            console.log("Centralized tabletTelemetryWebhook:", data);
            setTabletTelemetryData({ ...data });
        });

        socketInstance.on("tabletTransferTableWebhook", (data) => {
            console.log("Centralized tabletTransferTableWebhook:", data);
            setTabletTransferTableData({ ...data });
        });

        socketInstance.on("tabletTransferUpdated", (data) => {
            console.log("Centralized tabletTransferUpdated:", data);
            setTabletTransferUpdated({ ...data });
        });

        socketInstance.on("serviceRequestLineChanged", (data) => {
            console.log("Centralized serviceRequestLineChanged:", data);
        });
    
        socketInstance.on("error", (error) => {
            console.error("Socket.IO error:", error);
        });

        socketInstance.io.on("reconnect_attempt", (attempt) => {
            console.log("Reconnect attempt:", attempt);
            setReconnectAttempts(attempt);
        });        
    
        socketInstance.on("disconnect", (reason) => {
            console.log("Socket.IO disconnected:", reason);
            setSocket(null);
        });
    
        const cleanup = () => {
            console.log("Cleaning up Socket.IO...");
            socketInstance.removeAllListeners();
            socketInstance.disconnect();
        };
    
        window.addEventListener("beforeunload", cleanup);
    
        return () => {
            window.removeEventListener("beforeunload", cleanup);
            cleanup();
        };
    }, []);
    
    return (
        <WebSocketContext.Provider value={{ socket, assetTrackerData, levelMonitorData, holyKelData, serviceRequestData, alertData, jobState, linkedDevice, tabletTelemetryData, tabletTransferTableData, tabletTransferUpdated }}>
            {children}
        </WebSocketContext.Provider>
    );
};

// Hook to use Socket.IO connection
export const useWebSocket = () => {
    const socket = useContext(WebSocketContext);
    if (!socket && socket !== null) {
        throw new Error("useWebSocket must be used within a WebSocketProvider");
    }
    return socket;
};